import { useCallback } from 'react';
import { useTheme, Box } from '@mui/material';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import type { Container, Engine } from 'tsparticles-engine';

// DOCS: https://matthewaquino.medium.com/creating-an-interactive-background-using-the-tsparticles-library-in-a-react-app-d66d1fac3131

export const Background: React.FC = () => {
  const theme = useTheme();

  const particlesInit = useCallback(async (engine: Engine) => {
    await loadSlim(engine);
  }, []);

  const particlesLoaded = useCallback(
    async (_container: Container | undefined) => {
      // Optional callback when particles are loaded
    },
    []
  );

  // Convert hex to rgb for particle colors
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      };
    }
    return { r: 255, g: 102, b: 0 };
  };

  const primaryRgb = hexToRgb(theme.palette.text.primary);
  const secondaryRgb = hexToRgb(theme.palette.secondary.main);

  const options = {
    background: {
      color: {
        value: 'transparent',
      },
    },
    fpsLimit: 60,
    interactivity: {
      events: {
        onClick: {
          enable: true,
          mode: 'push',
        },
        onHover: {
          enable: true,
          mode: 'repulse',
        },
        resize: true,
      },
      modes: {
        push: {
          quantity: 10,
        },
        repulse: {
          distance: 100,
          duration: 0.4,
        },
      },
    },
    particles: {
      color: {
        value: [
          `rgb(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b})`,
          `rgb(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b})`,
        ],
      },
      links: {
        color: `rgb(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b})`,
        distance: 150,
        enable: true,
        opacity: 0.4,
        width: 1.5,
      },
      move: {
        enable: true,
        speed: 1,
        direction: 'none',
        random: false,
        straight: false,
        outMode: 'bounce',
      },
      number: {
        density: {
          enable: true,
          area: 800,
        },
        value: 50,
      },
      opacity: {
        value: 0.7,
        random: true,
        anim: {
          enable: true,
          speed: 1,
          opacity_min: 0.2,
        },
      },
      shape: {
        type: 'circle',
      },
      size: {
        value: 3,
        random: true,
        anim: {
          enable: true,
          speed: 2,
          size_min: 0.5,
        },
      },
    },
    detectRetina: true,
  };

  return (
    <>
      {/* Dark Smooth Background Layer */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -10,
          bgcolor: 'background.default',
        }}
      />

      {/* Interactive Particles Layer */}
      <Particles
        id="tsparticles"
        init={particlesInit}
        loaded={particlesLoaded}
        options={options as any} // eslint-disable-line @typescript-eslint/no-explicit-any
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -5,
        }}
      />
    </>
  );
};
